import {
  PosthogEventType,
  type LoadingState,
} from "@repo/app-types/client/client";
import type { Activity } from "@repo/app-types/entities/activity";
import type { Project } from "@repo/app-types/entities/project";
import { isValidEmail } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FileCard } from "@repo/ui/Cards/fileCard";
import { isValidName } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconFileUpload, IconRocket } from "@tabler/icons-react";
import { format } from "date-fns";
import { parse } from "papaparse";
import { useEffect, useState, type FC } from "react";
import { useDropzone } from "react-dropzone";
import { useImportParticipantsMutation } from "../../api/entities/interview.js";
import type { InterviewWithContactAndActivities } from "../../app/admin_client_types.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { captureAnalyticsEvent } from "../analytics/analyticsClient.js";
import {
  bulkUpdateInterviews,
  deleteInterviews,
} from "../interviews/interviewsSlice.js";
import { fetchProjectById } from "../projects/projectsSlice.js";
import {
  TargetingSlideoverTable,
  type RowErrorsMap,
} from "./targetingSlideoverTable.js";
import {
  TargetingSlideoverVariant,
  type TargetingContact,
} from "./targetingSlideoverTypes.js";

export interface TargetingSlideoverProps {
  project: Project;
  existingEmptyInterviews: InterviewWithContactAndActivities[];
  variant: TargetingSlideoverVariant;
  loadingState: LoadingState;
  onClose: () => void;
}

export const TargetingSlideover: FC<TargetingSlideoverProps> = ({
  project,
  existingEmptyInterviews,
  variant,
  onClose,
  loadingState,
}) => {
  const dispatch = useAppDispatch();
  const [importParticipants] = useImportParticipantsMutation();

  const [summaryError, setSummaryError] = useState<string | undefined>();
  const [rowErrors, setRowErrors] = useState<RowErrorsMap>({});

  // Store the contacts the user enters manually or from a CSV
  const [contacts, setContacts] = useState<TargetingContact[]>(emptyTableState);

  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

  const [
    isLiveParticipantsConfirmationModalOpen,
    setIsLiveParticipantsConfirmationModalOpen,
  ] = useState<boolean>(false);

  const [
    isCancelOutreachParticipantsConfirmationModalOpen,
    setIsCancelOutreachParticipantsConfirmationModalOpen,
  ] = useState<boolean>(false);

  // Re-compute errors whenever contacts change. If there are no errors,
  // summaryError will be an empty string and errorsMap an empty object.
  useEffect(() => {
    const { summaryError, errorsMap } = computeRowErrors(contacts);
    setRowErrors(errorsMap);
    setSummaryError(summaryError);
  }, [contacts]);

  const isAddingNewParticipants =
    variant === TargetingSlideoverVariant.MANUAL ||
    variant === TargetingSlideoverVariant.CSV;
  const isEditingPendingParticipants =
    variant === TargetingSlideoverVariant.PENDING_PARTICIPANTS;
  const isEditingOutreachParticipants =
    variant === TargetingSlideoverVariant.OUTREACH_PARTICIPANTS;
  const pendingInterviews = existingEmptyInterviews.filter(
    (i) => i.status === "pending",
  );
  const pendingInterviewContacts: TargetingContact[] = pendingInterviews
    .map((i) => {
      if (!i.contact.email) return null;

      const contact: TargetingContact = {
        email: i.contact.email,
        first_name: i.contact.first_name,
        last_name: i.contact.last_name,
        activity_summary: computeActivitySummary(i.activities),
      };
      return contact;
    })
    .filter((c): c is TargetingContact => Boolean(c));

  useEffect(() => {
    if (isEditingPendingParticipants || isEditingOutreachParticipants) {
      setContacts(pendingInterviewContacts);
    }
  }, [variant, loadingState]);

  // If we're editing outreach participants, any participants removed in the table
  // will need to be cancelled
  const cancelledInterviews =
    isEditingOutreachParticipants &&
    pendingInterviewContacts.filter((c) => {
      return !contacts.some((row) => row.email === c.email);
    });

  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
  } = useDropzone({
    onDropAccepted: (files) => {
      setSummaryError(undefined);
      setRowErrors({});
      const file = files[0]; // There will only ever be on 1 file since we set maxFiles to 1
      if (!file) {
        return;
      }
      setUploadedFile(file);

      // Parse the CSV content
      parse(file, {
        header: true,
        delimiter: ",",
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length) {
            setSummaryError(
              `Error parsing CSV: ${JSON.stringify(result.errors)}`,
            );
            return;
          }

          // Map parsed data to contact objects. Account for different column capitalization and naming conventions.
          const mappedContacts = result.data
            .filter((row: any) => findValueForColumnName(row, ["email"]))
            .map((row: any) => ({
              email: findValueForColumnName(row, ["email"]),
              first_name: findValueForColumnName(row, [
                "first_name",
                "firstname",
                "first-name",
                "first name",
                "first",
              ]),
              last_name: findValueForColumnName(row, [
                "last_name",
                "lastname",
                "last-name",
                "last name",
                "last",
              ]),
            }));

          // De-dupe contacts by email
          const uniqueContactsMap: Record<string, TargetingContact> = {};
          mappedContacts.forEach((contact) => {
            uniqueContactsMap[contact.email] = contact;
          });

          const uniqueContacts = Object.values(uniqueContactsMap);

          if (!mappedContacts.length) {
            setUploadedFile(undefined);
            setSummaryError(
              "No valid participants found in CSV. Please ensure your CSV has an 'email' column and at least one row with a valid email address.",
            );
            return;
          }

          const { summaryError, errorsMap } = computeRowErrors(mappedContacts);
          if (summaryError) {
            setSummaryError(summaryError);
            setRowErrors(errorsMap);
          }

          setContacts(uniqueContacts);
        },
      });
    },
    onDropRejected: (err) => {
      setSummaryError("File must be a CSV");
      setRowErrors({});
    },
    maxFiles: 1,
    maxSize: 10000000, // Max size 10MB
    accept: { "text/csv": [] },
  });

  const renderCsvUploadInstructions = () => {
    return (
      <div className="h-full overflow-y-scroll">
        <p className="mb-4 text-sm text-gray-600">
          CSV must include an "email" column, and can optionally include
          "first_name" and "last_name" columns. Providing at least 20
          participants is recommended.
        </p>
        <div
          {...getDropzoneRootProps()}
          className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
        >
          <div className="text-center">
            <IconFileUpload
              aria-hidden="true"
              className="mx-auto h-12 w-12 text-gray-300"
            />
            <div className="mt-4 flex text-sm leading-6 text-gray-600">
              <label
                className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 focus-within:outline-hidden hover:text-indigo-500"
                htmlFor="file-upload"
              >
                <span>Upload a CSV</span>
                <input
                  {...getDropzoneInputProps()}
                  className="sr-only"
                  id="file-upload"
                  name="file-upload"
                  type="file"
                />
              </label>
              <p className="pl-1">or drag and drop.</p>
            </div>
            <p className="text-xs leading-5 text-gray-600">Up to 10MB</p>
          </div>
        </div>
        {maybeRenderSummaryError()}
      </div>
    );
  };

  const renderBodyContent = () => {
    switch (variant) {
      case TargetingSlideoverVariant.CSV: {
        // If we don't have uploaded file yet, render the dropzone
        if (!uploadedFile) return renderCsvUploadInstructions();

        // Otherwise render the uploaded file
        return (
          <>
            <FileCard
              name={uploadedFile.name}
              onDelete={() => {
                setUploadedFile(undefined);
                setContacts([]);
              }}
            />
            <label
              className="my-2 block text-sm leading-6 font-medium text-foreground"
              htmlFor="csv-upload"
            >
              {contacts.length} participants found
            </label>
            <TargetingSlideoverTable
              contacts={contacts}
              rowErrors={rowErrors}
              variant={variant}
            />
            {maybeRenderSummaryError()}
          </>
        );
      }

      case TargetingSlideoverVariant.MANUAL:
        return (
          <>
            <p className="mb-4 text-sm text-gray-600">
              Enter or paste participant details in the table below. Providing
              at least 20 participants is recommended.
            </p>
            <TargetingSlideoverTable
              contacts={contacts}
              rowErrors={rowErrors}
              setContacts={setContacts}
              variant={variant}
            />
            {maybeRenderSummaryError()}
          </>
        );

      case TargetingSlideoverVariant.PENDING_PARTICIPANTS:
        return (
          <>
            <p className="mb-4 text-sm text-gray-600">
              Edit or remove participants in the table below.
            </p>
            <TargetingSlideoverTable
              contacts={contacts}
              rowErrors={rowErrors}
              setContacts={setContacts}
              variant={variant}
            />
            {maybeRenderSummaryError()}
          </>
        );

      case TargetingSlideoverVariant.OUTREACH_PARTICIPANTS: {
        return (
          <>
            <p className="mb-4 text-sm text-gray-600">
              Email outreach is in progress for the following participants.
              Reminder emails are automatically sent to participants two days
              after their initial invitation. Removing a participant from the
              table below will cancel their interview.
            </p>
            <TargetingSlideoverTable
              contacts={contacts}
              rowErrors={rowErrors}
              setContacts={setContacts}
              variant={variant}
            />
          </>
        );
      }
    }
  };

  // Return an object with row-specific errors as well as a summary error message
  const computeRowErrors = (
    rows: TargetingContact[],
  ): { summaryError: string; errorsMap: RowErrorsMap } => {
    // Max number of participants that can be added at once is 500
    if (rows.length > 500) {
      return {
        errorsMap: {},
        summaryError:
          "A maximum of 500 participants can be added at once. Please remove some participants and try again.",
      };
    }

    // Compute duplicate emails to be used by our row-specific helper function
    const emailSet = new Set();
    const duplicateEmails: string[] = [];
    rows.forEach((contact) => {
      if (emailSet.has(contact.email)) {
        duplicateEmails.push(contact.email);
      }
      emailSet.add(contact.email);
    });

    // Compute row-specific errors and store in our map of row indices to errors.
    const errorsMap: RowErrorsMap = {};
    rows.forEach((row, index) => {
      const errorForRow = computeErrorForRow(row, duplicateEmails);
      if (errorForRow) errorsMap[index] = errorForRow;
    });

    return {
      errorsMap,
      summaryError: computeSummaryErrorMessage(errorsMap, variant),
    };
  };

  const computeErrorForRow = (
    row: TargetingContact,
    duplicateEmails: string[],
  ) => {
    // Ignore empty rows (no email, first_name, and last_name)
    const isEmptyRow = !row.email && !row.first_name && !row.last_name;
    if (isEmptyRow) return "";

    // Ensure every contact has a valid email
    if (!row.email || !isValidEmail(row.email)) return "Invalid email address.";

    if (!isValidEmail(row.email, true))
      return "Email aliases are not supported.";

    const hasInvalidName =
      (row.first_name && !isValidName(row.first_name)) ||
      (row.last_name && !isValidName(row.last_name));
    if (hasInvalidName) {
      return "Names must be fewer than 36 characters.";
    }

    if (duplicateEmails.includes(row.email)) {
      return "Duplicate entries found for this email address";
    }

    const hasConflictingInProgressInterview = existingEmptyInterviews.some(
      (interview) =>
        interview.contact.email === row.email &&
        interview.status === "in_progress",
    );
    const hasConflictingPendingInterview = existingEmptyInterviews.some(
      (interview) =>
        interview.contact.email === row.email && interview.status === "pending",
    );

    if (hasConflictingInProgressInterview)
      return "An in-progress interview already exists for this participant.";

    if (
      hasConflictingPendingInterview &&
      variant !== TargetingSlideoverVariant.PENDING_PARTICIPANTS
    )
      return "A pending participant already exists for this contact.";

    return "";
  };

  const onSaveProject = async () => {
    // Ignore empty rows (no email, first_name, or last_name)
    const trimmedRows = contacts.filter(
      (contact) => contact.email || contact.first_name || contact.last_name,
    );

    if (isAddingNewParticipants && !trimmedRows.length) {
      setSummaryError("Please provide at least one participant.");
      return;
    }

    const { summaryError, errorsMap } = computeRowErrors(contacts);
    if (summaryError) {
      setRowErrors(errorsMap);
      setSummaryError(summaryError);
      return;
    }

    setIsSaveInProgress(true);

    try {
      // Only delete or cancel interviews if we're editing an existing list.
      if (isEditingPendingParticipants || isEditingOutreachParticipants) {
        // If we can't find an old pending interview in the new rows, the user removed it.
        const deletedPendingInterviewIds = pendingInterviews
          .filter((interview) => {
            return !trimmedRows.some(
              (row) => row.email === interview.contact.email,
            );
          })
          .map((contact) => contact.id);

        if (isEditingPendingParticipants) {
          await dispatch(
            deleteInterviews({
              projectId: project.id,
              interviewIds: deletedPendingInterviewIds,
            }),
          );
        }

        if (isEditingOutreachParticipants) {
          await dispatch(
            bulkUpdateInterviews({
              projectId: project.id,
              interviewIds: deletedPendingInterviewIds,
              update: {
                status: "cancelled",
              },
            }),
          );
        }
      }

      // Only create new contacts and interviews if we're adding new participants
      if (!isEditingOutreachParticipants && trimmedRows.length) {
        await importParticipants({
          projectId: project.id,
          contacts: trimmedRows.map((r) => ({
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name,
            phone_number: null,
            ext_id: null,
          })),
        }).unwrap();

        // Refetch project to get latest interview counts
        dispatch(fetchProjectById(project.id));

        captureAnalyticsEvent(PosthogEventType.ParticipantsInvite, {
          project_id: project.id,
          num_participants_invited: trimmedRows.length,
        });
      }

      // Dispatch a success notification
      showToast({
        title: `Participants successfully ${
          isAddingNewParticipants ? "added" : "updated"
        }`,
        variant: ToastVariant.Success,
      });
      closeSlideover();
    } catch (error: any) {
      // Dispatch a failure notification
      showToast({
        title: `Failed to ${
          isAddingNewParticipants ? "add" : "update"
        } participants to project`,
        description: error.message,
        variant: ToastVariant.Error,
      });
    } finally {
      setIsSaveInProgress(false);
    }
  };

  const maybeRenderSummaryError = () => {
    if (!summaryError) return null;

    return (
      <p className="mt-2 text-sm text-destructive" id="csv-upload-error">
        {summaryError}
      </p>
    );
  };

  // Reset state before closing the slideover
  const closeSlideover = () => {
    onClose();

    // Reset state after a short delay to allow the slideover to close first.
    setTimeout(() => {
      setUploadedFile(undefined);
      setContacts(emptyTableState);
      setRowErrors({});
      setSummaryError("");
    }, 1000);
  };

  const renderLiveParticipantsConfirmationModal = () => {
    return (
      <SimpleModal
        confirmButtonText="Save participants"
        icon={
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <IconRocket aria-hidden="true" className="h-6 w-6 text-blue-600" />
          </div>
        }
        isOpen={isLiveParticipantsConfirmationModalOpen}
        onCancel={() => {
          setIsLiveParticipantsConfirmationModalOpen(false);
        }}
        onConfirm={onSaveProject}
        subtitle="Heads up — because your project is currently Live, new participants will immediately receive interview invitation emails once they are saved."
        title="Confirm participants"
        variant={SimpleModalVariant.Standard}
      />
    );
  };

  const renderCancelOutreachParticipantsConfirmationModal = () => {
    return (
      <SimpleModal
        confirmButtonText="Save"
        isOpen={isCancelOutreachParticipantsConfirmationModalOpen}
        onCancel={() => {
          setIsCancelOutreachParticipantsConfirmationModalOpen(false);
        }}
        onConfirm={onSaveProject}
        subtitle={`If you proceed, ${
          cancelledInterviews && cancelledInterviews.length > 1
            ? `${cancelledInterviews && cancelledInterviews.length} interviews`
            : "1 interview"
        } will be cancelled.`}
        title="Cancel interviews?"
        variant={SimpleModalVariant.Warning}
      />
    );
  };

  return (
    <Slideover
      buttons={
        <>
          <Button onClick={closeSlideover} variant={ButtonVariant.Secondary}>
            Cancel
          </Button>
          <Button
            isDisabled={
              Object.values(rowErrors).length > 0 ||
              (isAddingNewParticipants &&
                !contacts.filter((c) => c.email).length)
            }
            isLoading={isSaveInProgress}
            onClick={() => {
              // If we're editing participants that have already been invited,
              // we might need to warn the user that they're cancelling interviews.
              if (isEditingOutreachParticipants) {
                if (cancelledInterviews && cancelledInterviews.length > 0) {
                  setIsCancelOutreachParticipantsConfirmationModalOpen(true);
                  return;
                }
                closeSlideover();
                return;
              }

              // If the project is live, show a confirmation modal before saving
              if (project.is_live)
                setIsLiveParticipantsConfirmationModalOpen(true);
              else onSaveProject();
            }}
            variant={ButtonVariant.Primary}
          >
            Save
          </Button>
        </>
      }
      onClose={() => {
        closeSlideover();
      }}
      shouldShow={Boolean(variant)}
      title={computeSlideoverTitle(variant)}
    >
      <div className="flex h-full flex-col overflow-y-scroll p-6">
        <div className="grid h-full grid-rows-[auto_auto_1fr_auto]">
          {renderBodyContent()}
        </div>
      </div>

      {renderLiveParticipantsConfirmationModal()}
      {renderCancelOutreachParticipantsConfirmationModal()}
    </Slideover>
  );
};

// 20 rows of empty data for the targeting table by default.
const emptyTableState = [
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
  { first_name: "", last_name: "", email: "" },
];

// Given a row and a list of valid column names, return the first value
// we can find for a valid column name. Useful for parsing CSVs that might have
// "firstName" instead of "first_name" for example.
function findValueForColumnName(row: any, possibleNames: string[]): any {
  for (const name of possibleNames) {
    const foundKey = Object.keys(row).find(
      (key) => key.replace(/[\s_-]/g, "").toLowerCase() === name,
    );
    if (foundKey) {
      return row[foundKey];
    }
  }
}

function computeSummaryErrorMessage(
  errorsMap: RowErrorsMap,
  variant: TargetingSlideoverVariant,
): string {
  // Offset by +1 to make human readable
  const errorIndices = Object.keys(errorsMap).map((index) => Number(index) + 1);
  const numErrors = errorIndices.length;

  if (!numErrors) {
    return "";
  }

  let baseError;
  if (numErrors === 1) {
    baseError = `Error found for row ${errorIndices[0]}`;
  } else {
    baseError = `Errors found for rows ${errorIndices.join(", ")}`;
  }

  return variant === TargetingSlideoverVariant.CSV
    ? `${baseError}. Please resolve errors and re-upload your CSV.`
    : baseError;
}

function computeSlideoverTitle(variant: TargetingSlideoverVariant) {
  switch (variant) {
    case TargetingSlideoverVariant.CSV:
    case TargetingSlideoverVariant.MANUAL:
      return "Add participants";
    case TargetingSlideoverVariant.PENDING_PARTICIPANTS:
      return "Edit participants";
    case TargetingSlideoverVariant.OUTREACH_PARTICIPANTS:
      return "Participant outreach";
  }
}

function computeActivitySummary(activities: readonly Activity[]) {
  const firstActivity = activities[0];
  if (!firstActivity) return undefined;

  if (activities.length === 1) {
    return [
      `Invite sent ${format(new Date(firstActivity.created_at), "MMM d, yyyy")}`,
    ];
  }

  const sortedActivities = activities.slice().sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateA.getTime() - dateB.getTime();
  });

  const oldestActivity = sortedActivities[0];
  const latestActivity = sortedActivities[sortedActivities.length - 1];
  if (!oldestActivity || !latestActivity) return undefined;

  const oldestActivityDate = new Date(oldestActivity.created_at);
  const latestActivityDate = new Date(latestActivity.created_at);

  return [
    `Invite sent ${format(oldestActivityDate, "MMM d, yyyy")}`,
    `Reminder sent ${format(latestActivityDate, "MMM d, yyyy")}`,
  ];
}
